/**
 * MCP tool catalog — the single source of truth for the tools the infras MCP
 * server exposes to AI agents AND for the `/mcp` docs page. The MCP server
 * registers these (execution handlers live in apps/mcp); the web renders them.
 *
 * The inventory tools are READ-ONLY — secret tools return NAMES ONLY (values are
 * never stored, so they cannot be read). Writes are flagged `writes: true` and come
 * in two shapes:
 *   · access requests — an agent may FILE, COMMENT and withdraw its OWN request, but
 *     the approve/deny DECISION stays a human UI action (there is no `decide` tool).
 *   · curated data — `update_repo` (product line / service / Owner / Maintainer) and
 *     `set_key_note` (a config key's description). Both are root|maintainer, gated in
 *     rbac.ts `MAINTAINER_MCP_TOOLS`, and hidden from a lower role's tools/list.
 *
 * Curated writes use ONE `update_<resource>` tool per resource, not one tool per
 * field — tools/list is context paid on every request, and two tools with
 * overlapping descriptions make the model pick wrong. Scanner-owned columns are
 * never settable: the scan rewrites them each pass, so the edit would revert.
 */

import {
  ACCESS_DURATION_DAYS,
  ACCESS_REQUEST_RESOURCE_NAME_MAX,
  ACCESS_RESOURCE_TYPES,
} from './access-requests';
import { PEER_TIMELINE_KINDS } from './vpn';

export interface McpToolParam {
  type: 'string' | 'number' | 'boolean' | 'array';
  description: string;
  /** Element schema — REQUIRED when type is 'array'. Without it the model gets an
   *  untyped list and guesses at the element shape. */
  items?: { type: 'string' | 'number'; enum?: readonly (string | number)[] };
  /** Optional JSON Schema maxLength (string params). */
  maxLength?: number;
  /**
   * Optional JSON Schema enum — the closed set of accepted values. Worth setting
   * whenever the server validates against a list: the model then picks a legal
   * value instead of inventing one and learning the constraint from an error.
   */
  enum?: readonly (string | number)[];
}

export interface McpTool {
  name: string;
  title: string;
  description: string;
  category:
    | 'search'
    | 'repos'
    | 'clusters'
    | 'databases'
    | 'secrets'
    | 'scheduler'
    | 'catalog'
    | 'access'
    | 'tasks'
    | 'errors'
    | 'alerts'
    | 'vpn';
  /** True for tools that MUTATE (task / access-request create, comment, edit). Omitted = read-only. */
  writes?: boolean;
  inputSchema: {
    type: 'object';
    properties: Record<string, McpToolParam>;
    required?: string[];
    additionalProperties?: boolean;
  };
  /** A representative call, for the docs page. */
  example: string;
}

/**
 * The timeline kinds `get_device_audit_log` returns. The two peer-RECORD kinds
 * (`peer_registered`, `peer_updated`) come from the Mongo peer document, which the
 * MCP process cannot read — they appear on the web Peers page only.
 */
export const DEVICE_AUDIT_LOG_KINDS = PEER_TIMELINE_KINDS.filter(
  (k) => k !== 'peer_registered' && k !== 'peer_updated',
);
export const DEVICE_AUDIT_LOG_DEFAULT_LIMIT = 200;
export const DEVICE_AUDIT_LOG_MAX_LIMIT = 500;

const obj = (
  properties: Record<string, McpToolParam>,
  required: string[] = [],
): McpTool['inputSchema'] => ({ type: 'object', properties, required, additionalProperties: false });

export const MCP_TOOLS: McpTool[] = [
  {
    name: 'search',
    title: 'Global search',
    description:
      'Search the whole infra inventory (repos, clusters, namespaces, services, and secret/key names) by keyword. Workloads are deliberately not indexed (they duplicated their repo) — use get_repo or service_graph for "where does it run". Substring + full-text + typo-tolerant trigram. Each hit carries a `tier` saying WHERE it matched — 5 exact title, 4 title, 3 body (a secret bundle\'s env-var key names), 2 subtitle, 1 token-only, 0 fuzzy near-miss — and results are ordered by tier, then by most-recent GitLab activity within a tier, live repos before archived. A tier-0 hit means the term appears nowhere in the row, so treat it as a suggestion, not an answer. If `withheld` is above zero, secret matches exist that your access level hides: the names are withheld, not their existence, so ask for access rather than reporting that they do not exist. Use this first when you only have a name/fragment (e.g. an app name, a config key like MONGO_SMALLAPP_URL, a cluster).',
    category: 'search',
    inputSchema: obj(
      {
        query: { type: 'string', description: 'Keyword or fragment (>=2 chars).' },
        limitPerType: { type: 'number', description: 'Max hits per entity type (default 5, max 25).' },
        entityType: {
          type: 'string',
          description:
            'Narrow to one doc type: repo | secret | cluster | namespace | service | catalog | errorcode. Omit for all types. An unknown value returns nothing rather than everything.',
        },
      },
      ['query'],
    ),
    example: 'search(query: "canva")  ·  search(query: "MONGO_SCIO", entityType: "secret")',
  },
  {
    name: 'list_repos',
    title: 'List repos',
    description:
      'List GitLab repos in the inventory, with curation facets. Filter by product line (from list_product_lines), group path, kind, owner-status, and archived-status. ARCHIVED repos are EXCLUDED by default (archived: "active") — they are not the daily curation surface; pass archived: "all"/"archived" to include/isolate them. Returns path, gitlabProjectId (the id update_repo takes), group, visibility, default branch, product line, kind, the assigned owner + maintainer emails, and how many k8s deployments run the repo image.',
    category: 'repos',
    inputSchema: obj({
      productLine: { type: 'string', description: 'Filter by product line (exact; e.g. smallapp, scio-core, devices — see list_product_lines).' },
      group: { type: 'string', description: 'Filter by group_path (substring).' },
      classification: {
        type: 'string',
        description: 'Filter by kind: service | api | job | frontend | lib, or "unknown" (no deployed service).',
      },
      needsOwner: { type: 'boolean', description: 'Only repos with NO assigned owner (the curation backlog).' },
      archived: { type: 'string', description: 'active (default — exclude archived) | archived (only archived) | all.' },
      limit: { type: 'number', description: 'Max rows (default 100).' },
    }),
    example: 'list_repos(productLine: "smallapp", needsOwner: true)',
  },
  {
    name: 'get_repo',
    title: 'Get repo detail',
    description:
      "The one-shot 'what / where / deploy / env' answer for a repo (by path or last segment). WHAT: product line + confidence + kind + the service/catalog it belongs to, plus its assigned owner (accountable) + maintainer (incident contact). WHERE + DEPLOY: every k8s deployment of its image — cluster, namespace, image tag, replicas. ENV: the env-var NAMES on each Deployment spec, plus inheritedConfig (var names from its shared @optisigns packages). Names only — never values.",
    category: 'repos',
    inputSchema: obj(
      { name: { type: 'string', description: 'Repo path or last path segment (e.g. optisigns-smallapp-rest-canva).' } },
      ['name'],
    ),
    example: 'get_repo(name: "optisigns-open-api")',
  },
  {
    name: 'list_clusters',
    title: 'List clusters',
    description:
      'List all Kubernetes clusters: name, cloud/DO account, region, k8s version, LB IP, node count, reachability, and the curated description of what runs there.',
    category: 'clusters',
    inputSchema: obj({}),
    example: 'list_clusters()',
  },
  {
    name: 'get_cluster',
    title: 'Get cluster detail',
    description:
      'Full detail for one cluster by name: topology (region, version, nodes, LB IP), the curated description, and its namespaces.',
    category: 'clusters',
    inputSchema: obj({ name: { type: 'string', description: 'Cluster name (e.g. k8s-optisigns-prod-smallapp-rest).' } }, ['name']),
    example: 'get_cluster(name: "k8s-optisigns-prod-smallapp-rest")',
  },
  {
    name: 'cluster_databases',
    title: 'Which databases a cluster may reach',
    description:
      'What ONE Kubernetes cluster is ALLOWED to reach: every DigitalOcean managed database and MongoDB Atlas project whose firewall / access list admits it, with the health of that permission. An edge means a trusted-source firewall rule or an access-list entry PERMITS the cluster — it does NOT mean anything connects (no workload environment is read). `health` is derived from the agent fleet: green = every agent of the cluster is heartbeating (15-min clock) AND its address is on the list; red = one is silent or missing from it; amber = some node runs no agent; neutral = nobody has read that list yet (never "fine"). `evidence` says WHY the pair exists: k8s_rule (a DO rule naming the cluster uuid, same-team only), ip_rule (a node address), atlas_entry (the address is on the access list), mapping (authorized, no address whitelisted yet). NEVER returns connection strings, hosts, users or passwords. An unknown cluster returns an empty list, not an error.',
    category: 'clusters',
    inputSchema: obj(
      { cluster: { type: 'string', description: 'Cluster name (e.g. k8s-optisigns-sandbox-transforge).' } },
      ['cluster'],
    ),
    example: 'cluster_databases(cluster: "k8s-optisigns-sandbox-transforge")',
  },
  {
    name: 'list_do_databases',
    title: 'List DigitalOcean managed databases',
    description:
      'List DigitalOcean managed databases (Postgres, Valkey, …) per DO team as last scanned by the read-only do_db_firewall scanner: uuid, name, engine, version, region, status, team, node count, size and how many trusted-source firewall rules it has. NO connection strings, hosts or users — those are never stored. Filter by team (a do_accounts slug such as optisigns-lite) and/or engine (pg, valkey).',
    category: 'databases',
    inputSchema: obj({
      team: { type: 'string', description: 'DO team slug (e.g. optisigns-lite, optisigns-devqa).' },
      engine: { type: 'string', description: 'Engine filter (e.g. pg, valkey, mysql).' },
    }),
    example: 'list_do_databases(team: "optisigns-lite")',
  },
  {
    name: 'get_do_database',
    title: 'Get DigitalOcean database firewall',
    description:
      'One DigitalOcean managed database by name, with its trusted-source firewall rules (ip_addr / k8s / droplet / tag / app) as last scanned. Read-only inventory — use it to answer "which IPs and clusters may reach this database". Never returns credentials.',
    category: 'databases',
    inputSchema: obj({ name: { type: 'string', description: 'Database name (e.g. optisigns-internal).' } }, ['name']),
    example: 'get_do_database(name: "optisigns-internal")',
  },
  {
    name: 'list_secrets',
    title: 'List secrets',
    description:
      'List secrets (NAMES ONLY — no values). Filter by env (dev/sandbox/qa/beta/prod) and/or provider (gcp/aws). Shows whether each is a JSON bundle and how many keys it holds.',
    category: 'secrets',
    inputSchema: obj({
      env: { type: 'string', description: 'dev | sandbox | qa | beta | prod (matches derived env).' },
      provider: { type: 'string', description: 'gcp | aws.' },
      limit: { type: 'number', description: 'Max rows (default 100).' },
    }),
    example: 'list_secrets(env: "qa")',
  },
  {
    name: 'get_secret',
    title: 'Get secret detail',
    description:
      'Full detail for one logical secret by (canonical) name — its per-environment instances (project, env, created), the union of its key NAMES with which envs each key appears in, PLUS each key\'s editorial description (markdown context: what the key is, who owns it, which app uses it, where to set non-prod/prod values). Values are never returned; descriptions are names+context only. Set a key description with set_key_note.',
    category: 'secrets',
    inputSchema: obj({ name: { type: 'string', description: 'Secret canonical name (bundles collapse to <path>--keys).' } }, ['name']),
    example: 'get_secret(name: "optisigns-open-api--keys")',
  },
  {
    name: 'find_secret_key',
    title: 'Find a config key',
    description:
      'Answer "where does config key X live?" — given a key NAME (e.g. STRIPE_KEY, MONGO_SMALLAPP_URL), return every secret/bundle that contains it (provider, project, env) plus its editorial description (markdown context) if one is set. Values are never returned.',
    category: 'secrets',
    inputSchema: obj({ keyName: { type: 'string', description: 'Exact config key name to locate.' } }, ['keyName']),
    example: 'find_secret_key(keyName: "MONGO_SMALLAPP_URL")',
  },
  {
    name: 'set_key_note',
    title: 'Set a config-key description',
    description:
      'Set (or clear) the editorial DESCRIPTION of a config KEY — the markdown context surfaced to humans (secret-detail page) and AI (get_secret / find_secret_key). Write what the key is, who owns it, which app consumes it, and where to set its non-prod vs prod value. GLOBAL by key name (a key\'s meaning is intrinsic, so one description covers every bundle/env it appears in). Markdown is supported. Empty note clears it. Requires a root or maintainer PAT. Never include a secret VALUE — this is names + context only.',
    category: 'secrets',
    writes: true,
    inputSchema: obj(
      {
        keyName: { type: 'string', description: 'Exact config key name to describe (e.g. APP_SECRET_KEY).' },
        note: { type: 'string', description: 'Markdown description (context, NOT the value). Empty string clears it. Max 20000 chars.' },
      },
      ['keyName', 'note'],
    ),
    example: 'set_key_note(keyName: "APP_SECRET_KEY", note: "## APP_SECRET_KEY\\nSmallApp HMAC signing secret. Owner: Platform. Used by open-api + smallapp REST. Prod: shared--keys; non-prod: <env> keyset.")',
  },
  {
    name: 'list_jobs',
    title: 'List scheduler jobs',
    description:
      'List the scheduler jobs (scanners that keep the inventory fresh): name, what it does, cron schedule, enabled state, and the last run — status, time, duration and error. Use get_job for run history and timing percentiles.',
    category: 'scheduler',
    inputSchema: obj({}),
    example: 'list_jobs()',
  },
  {
    name: 'run_job',
    title: 'Run a scheduler job now',
    description:
      'Enqueue a ONE-OFF run of a scheduler job, the same way the dashboard\'s "Run now" button does. ROOT PAT ONLY. Fire-and-forget: this returns once the queue accepts the job, NOT when the run finishes — poll get_job to see the outcome, which is where success, failure and duration appear. A job that is DISABLED still runs (that is deliberate — Run now works on disabled jobs), so check list_jobs first if that matters. Singleton jobs already running produce a `skipped` run rather than a second execution. Prefer letting a job keep its schedule; use this to verify a fix immediately after a deploy, or to refresh inventory on demand. Note that some jobs act OUTSIDE this app — atlas-whitelist-reconcile writes MongoDB Atlas access lists and secret-inventory triggers a GitLab pipeline — and a run cannot be recalled once dispatched.',
    category: 'scheduler',
    writes: true,
    inputSchema: obj(
      {
        name: { type: 'string', description: 'Job name, exactly as returned by list_jobs (e.g. transforge-stats).' },
      },
      ['name'],
    ),
    example: 'run_job(name: "transforge-stats")',
  },
  {
    name: 'get_job',
    title: 'Get scheduler job detail',
    description:
      'One job: its definition (what it does, cron, enabled, singleton, timeout, params) plus recent run history and a timing summary — runs, success rate, p50/p95/max duration, and the interval actually observed between runs. This is the tool for "is this job healthy", "what does a run cost", and "is its cron too slow/too aggressive" — answer those from here rather than from cluster access. Per-run log lines are not included; the dashboard job page has the log viewer.',
    category: 'scheduler',
    inputSchema: obj(
      {
        name: { type: 'string', description: 'Job name, as returned by list_jobs (e.g. gitlab-scan).' },
        limit: { type: 'number', description: 'How many recent runs to return (default 20, max 200). The timing summary covers exactly these runs.' },
      },
      ['name'],
    ),
    example: 'get_job(name: "gitlab-scan", limit: 50)',
  },
  {
    name: 'list_catalog',
    title: 'List deploy catalog',
    description:
      'List deployment-catalog entries — "how we deploy a class of application" (vs the inventories, which record what exists). Call this BEFORE deploying or bootstrapping an app to find the recipe entry (e.g. smallapp-v3), then get_catalog for the full recipe.',
    category: 'catalog',
    inputSchema: obj({}),
    example: 'list_catalog()',
  },
  {
    name: 'get_catalog',
    title: 'Get deploy recipe',
    description:
      'Full deploy recipe for one catalog entry by name: layers (rest/jobs/screen), build (images, registry, tags), secret bundle convention + deploy scripts, k8s placement, runbook, known issues, AND a per-environment DEPLOYMENT MATRIX (spec.environments) — one fully-specified row per env with cluster + DO cluster id, namespace, kubeconfig secret, backend URL, ingress name/LB-IP/TLS secret, CDN URL + S3 bucket + CloudFront id, GCP secrets project, per-app K8s Secret, and the --keys bundle. Read spec.environments to locate or re-verify a deployment; pick the row for your target env.',
    category: 'catalog',
    inputSchema: obj(
      { name: { type: 'string', description: 'Catalog entry name (from list_catalog), e.g. smallapp-v3.' } },
      ['name'],
    ),
    example: 'get_catalog(name: "smallapp-v3")',
  },
  {
    name: 'list_error_codes',
    title: 'List error codes',
    description:
      'The customer error dictionary: every code a customer can be shown, per lane. A code is layer 2 of the emitter contract — the STABLE, customer-facing half. Layer 1 is `reason`, an internal routing key that alert rules filter on and that may be renamed at any time; never quote a reason to a customer. Returns code, reason, lane, level, taxonomy class and the customer copy.',
    category: 'errors',
    inputSchema: obj({
      lane: { type: 'string', description: 'Narrow to one lane key, e.g. "mdm". Omit for all lanes.' },
    }),
    example: 'list_error_codes()  ·  list_error_codes(lane: "mdm")',
  },
  {
    name: 'list_log_lanes',
    title: 'List emitter lanes',
    description:
      'Every structured-logging lane in the estate: its `evt` (the token every Loki rule prefilters on, e.g. |= "mdm_fail"), owning repo, contract module, and whether it is a backend lane (queried | regexp) or a browser/Faro lane (queried | logfmt). The unit is the SURFACE, not the repo — open-api owns two lanes and scio3 owns three. ⚠️ Naming is inconsistent (six *_fail, three *_error), so a query that assumes _fail silently matches nothing against the Faro lanes. Use this to find which lane a log line belongs to, or which module owns a taxonomy.',
    category: 'errors',
    inputSchema: obj({}),
    example: 'list_log_lanes()',
  },
  {
    name: 'get_error_code',
    title: 'Explain an error code',
    description:
      'Resolve ONE code (or the internal `reason` behind it) to the exact copy a customer should be given, plus the renderer rules for it. USE THE `customerMessage` VERBATIM — it is reviewed copy, and several codes are deliberately generic because the honest cause is our own infrastructure or is not attributable to one account. Do not improve it, do not add the cause, and never surface the `reason`, the `note`, or any raw log field to a customer. Read `note` before rendering: some carry hard rules, e.g. `unsupported_request` must not be shown unless the event belongs to the asking account.',
    category: 'errors',
    inputSchema: obj(
      { code: { type: 'string', description: 'The customer code (e.g. device_clock_out_of_sync) OR the internal reason (e.g. auth_signature_expired).' } },
      ['code'],
    ),
    example: 'get_error_code(code: "device_clock_out_of_sync")',
  },
  {
    name: 'list_services',
    title: 'List services',
    description:
      'List classified services (apps), optionally filtered by PRODUCT LINE. Returns name, product line, kind, classification confidence + source, member repo count, and how many clusters it runs in. This is the entry point for the product-line model — answers "what apps are in the <product line> line?" — then service_graph for one service\'s placement.',
    category: 'repos',
    inputSchema: obj({
      productLine: { type: 'string', description: 'Filter to one product line (e.g. scio-core | ads-portal | optisound | smallapp).' },
      limit: { type: 'number', description: 'Max rows (default 100).' },
    }),
    example: 'list_services(productLine: "smallapp")',
  },
  {
    name: 'service_graph',
    title: 'Which app runs where',
    description:
      'The service→workload→cluster spine ("which app runs where"). Returns graph nodes (service / workload / cluster) + edges. Filter by `service` (where does this service run) OR `cluster` (what runs in this cluster). Edge confidence: deployedTo (service→workload) is "confirmed" (exact image==repo path), "monorepo" (a deterministic sub-path image of a multi-image repo), or "inferred" (genuinely fuzzy — none today); runsOn (workload→cluster) is always "confirmed". Only classified services appear — use list_repos(classification:"unknown") to find gaps and update_repo to fill them.',
    category: 'repos',
    inputSchema: obj({
      service: { type: 'string', description: 'Focus one service by name (from list_repos / services).' },
      cluster: { type: 'string', description: 'Focus one cluster by name (what services run in it).' },
    }),
    example: 'service_graph(service: "ads-portal-open-api")',
  },
  {
    name: 'list_assignable_users',
    title: 'People who can be assigned as repo Owner/Maintainer',
    description:
      "The infras-app users a repo's owner or maintainer can be set to (anyone who has signed in or holds a role). Call this BEFORE update_repo to get the exact email — update_repo rejects an email that is not on this list, so a typo fails loudly instead of creating a phantom owner.",
    category: 'repos',
    inputSchema: obj({}),
    example: 'list_assignable_users()',
  },
  {
    name: 'preview_incident_notification',
    title: 'Who would be emailed if this service had an incident',
    description:
      "READ-ONLY. Answers \"if <service> broke right now, who would be emailed, and what would the email say\" — and SENDS NOTHING. There is deliberately no tool that sends: a human presses the button in the dashboard (Operations \u2192 Notify owners). Same posture as access requests, where an agent may file and recommend but approve/deny stays a human action; waking people at 03:00 is at least as consequential.\n\nAUDIENCE: a service has no owners of its own, so this resolves service \u2192 its repos \u2192 those repos' owner/maintainer/engineer rosters, deduped by email.\n\nREAD `source` BEFORE REPORTING THE RESULT. 'owners' means these people are actually responsible. 'layer1_fallback' means NOBODY owns the service and the default access assignees would be emailed instead — that is the COMMON case (most repos have no owner assigned), and reporting those names as \"the owners\" would be wrong. `fallbackReason` says why. `unreachable` lists people who ARE assigned but have no email on file — a real gap worth naming, not an empty set to ignore.",
    category: 'repos',
    inputSchema: obj(
      {
        service: {
          type: 'string',
          description:
            "Service name (case-insensitive) — from list_services or search. NOT a repo path.",
        },
      },
      ['service'],
    ),
    example: 'preview_incident_notification(service: "optisigns-open-api")',
  },
  {
    name: 'update_repo',
    title: "Update a repo's curated fields",
    description:
      "Update any of a repo's CURATED fields in one call. Omitted field = unchanged; pass an EMPTY STRING to clear `owner`, or an EMPTY LIST to clear `maintainers`/`engineers`. All supplied fields apply in ONE transaction — a rejected value means nothing is written, never a half-update.\n\nCLASSIFICATION: `productLine` is the primary bucket — use an EXISTING line from list_product_lines (don't invent one; that causes taxonomy sprawl). product_line and kind both auto-derive from the repo path, so pass `productLine` only to OVERRIDE a wrong heuristic — that override is authoritative and survives every subsequent scan/stitch. `classification` is the kind (service | api | job | frontend | lib). One repo ↔ one service, so `serviceName` MOVES the repo. Call list_needs_review for the low-confidence backlog.\n\nOWNERSHIP: `owner` = accountable stakeholder/decision-maker (exactly ONE person — re-assigning REPLACES the incumbent). `maintainers` = incident contacts and `engineers` = who to pull in to work on it; both are ROSTERS taking a list, and the list you pass becomes the ENTIRE membership — so pass the full set you want, not a delta, and a repeated identical call is idempotent. Read the current roster from get_repo/list_repos first if you mean to add to it rather than replace it. Every email must come from list_assignable_users (assigning someone who has never signed in is possible, but only from the dashboard).\n\nNOT settable: path, group, visibility, defaultBranch, archived, lastActivityAt — the GitLab scanner owns those and rewrites them every pass, so an edit here would silently revert. Root/maintainer only; human-assigned values survive rescans. Read the result back with get_repo or list_repos.",
    category: 'repos',
    writes: true,
    inputSchema: obj(
      {
        gitlabProjectId: { type: 'number', description: "The repo's GitLab project id (from get_repo or list_repos)." },
        productLine: { type: 'string', description: 'Product line (primary bucket). Use an existing value from list_product_lines; overrides the path heuristic when wrong.' },
        classification: { type: 'string', description: 'Kind: service | api | job | frontend | lib. Auto-derived from the path if omitted.' },
        serviceName: { type: 'string', description: "Service to group the repo under. Defaults to the repo's short name. Changing it MOVES the repo." },
        catalogName: { type: 'string', description: 'Deploy-catalog name (e.g. smallapp-v3), if applicable.' },
        description: { type: 'string', description: 'Human note about the service.' },
        owner: { type: 'string', description: "Accountable stakeholder, by email (from list_assignable_users). Empty string clears the role." },
        maintainers: {
          type: 'array',
          items: { type: 'string' },
          description:
            "Incident contacts, by email (from list_assignable_users). The list REPLACES the whole roster — pass every maintainer you want, not just the new one. Empty list clears the role.",
        },
        engineers: {
          type: 'array',
          items: { type: 'string' },
          description:
            "Engineers to pull in for work on this repo, by email (from list_assignable_users). The list REPLACES the whole roster. Empty list clears the role.",
        },
      },
      ['gitlabProjectId'],
    ),
    example: 'update_repo(gitlabProjectId: 12345, productLine: "smallapp", owner: "someone@optisigns.com")',
  },
  {
    name: 'list_needs_review',
    title: 'Classification review queue',
    description:
      'REPOS needing a product line — either unclassified, or a low-confidence (< 70) non-human derivation. The dashboard "Needs classification" backlog. Returns repo path, gitlabProjectId, current product line, confidence, source. Archived repos are excluded. Resolve each with update_repo (pass a productLine from list_product_lines). Human/root confirmations are authoritative and never appear here.',
    category: 'repos',
    inputSchema: obj({ limit: { type: 'number', description: 'Max rows (default 100).' } }),
    example: 'list_needs_review()',
  },
  {
    name: 'list_product_lines',
    title: 'List product lines',
    description:
      'The product-line taxonomy — the valid buckets update_repo accepts for `productLine`, with how many repos are in each (total + active). Call this BEFORE curating so you assign an EXISTING line rather than inventing a near-duplicate (which fragments the taxonomy).',
    category: 'repos',
    inputSchema: obj({}),
    example: 'list_product_lines()',
  },
  {
    name: 'bulk_assign_owner',
    title: 'Bulk-assign owner/maintainer by filter',
    description:
      'Assign ONE person as owner or maintainer across every repo matching a filter — the "all smallapps are managed by Alice" workflow, in one call instead of updating each repo. Supply a filter (productLine and/or group; a naked call with no filter is refused) + role + email (from list_assignable_users). BEST-EFFORT: each repo\'s authorization is re-checked and a per-repo failure lands in `skipped`, not aborting the rest. Re-assigning REPLACES the current holder (idempotent). Archived repos are never touched. Every success is audited with the prior owner + the filter scope. root|maintainer only.',
    category: 'repos',
    writes: true,
    inputSchema: obj(
      {
        role: { type: 'string', description: "'owner' (accountable stakeholder) or 'maintainer' (incident contact)." },
        email: { type: 'string', description: 'The person to assign, by email (must be on list_assignable_users).' },
        productLine: { type: 'string', description: 'Filter: only repos in this product line (from list_product_lines).' },
        group: { type: 'string', description: 'Filter: only repos whose group_path contains this substring.' },
        onlyUnowned: { type: 'boolean', description: 'Only assign repos that currently have NO owner (fill the gaps, don\'t reassign).' },
      },
      ['role', 'email'],
    ),
    example: 'bulk_assign_owner(role: "owner", email: "alice@optisigns.com", productLine: "smallapp", onlyUnowned: true)',
  },
  {
    name: 'create_access_request',
    title: 'File an access request',
    description:
      'Access to a company system → this tool; anything else → create_task. File a request for access to a company resource, on behalf of the signed-in user (the PAT owner is the requester). Use this instead of pinging people on Slack. It is filed as an access TASK: the default access assignees are always assigned and emailed, plus anyone you name in extraReviewerEmails; any assignee (whatever their role) or a root/maintainer decides; the task is tracked with a full timeline and gets a clear approved/denied answer in the dashboard. You do NOT need a role to file. Note: the approve/deny decision is a human UI action — this tool cannot approve. If the resource type has required questions, filing will fail with what is missing; file from the dashboard for those.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      {
        resourceType: {
          type: 'string',
          enum: ACCESS_RESOURCE_TYPES,
          description: `Which system. One of: ${ACCESS_RESOURCE_TYPES.join(' | ')}.`,
        },
        resourceName: {
          type: 'string',
          description: `Optional precise target (DB name, Grafana host, GCP project id…) — defaults to the system's own label when omitted. Max ${ACCESS_REQUEST_RESOURCE_NAME_MAX} characters.`,
          maxLength: ACCESS_REQUEST_RESOURCE_NAME_MAX,
        },
        maintainerEmail: { type: 'string', description: 'Deprecated and ignored — there is no maintainer sign-off any more. Mention the owner in reason instead.' },
        reason: { type: 'string', description: 'Why you need access. Specific reasons are approved faster. Name the exact target here if you left resourceName blank.' },
        durationKind: {
          type: 'string',
          enum: ['permanent', 'temporary'],
          description:
            "Defaults to 'permanent'. Prefer 'temporary' with a durationDays: the task's due date is set to now + durationDays and the grant is valid until then.",
        },
        durationDays: {
          type: 'number',
          enum: ACCESS_DURATION_DAYS,
          description: `Required when durationKind is 'temporary', and rejected otherwise. One of: ${ACCESS_DURATION_DAYS.join(', ')}.`,
        },
        extraReviewerEmails: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Optional extra assignees (emails) added ON TOP of the default access assignees, who are always assigned. Anyone with a staff account may be added, whatever their role — but not the requester. Every assignee may approve or deny.',
        },
      },
      ['resourceType', 'reason'],
    ),
    example:
      'create_access_request(resourceType: "database", resourceName: "analytics replica DB", reason: "debug a prod incident", durationKind: "temporary", durationDays: 7)',
  },
  {
    name: 'list_access_requests',
    title: 'List access requests',
    description:
      'List access requests. scope "mine" = the requests you filed. scope "review" = every request, from every requester. Every role (root, maintainer, engineer, new_member) can use both scopes. Seeing a request is open to everyone; deciding it is for its assignees (any role) or root/maintainer, in the dashboard. Optionally filter by status. Use this to triage, then get_access_request for the full detail before recommending.',
    category: 'access',
    inputSchema: obj(
      {
        scope: { type: 'string', description: 'mine (requests you filed) | review (every request).' },
        status: { type: 'string', description: 'Optional: pending | approved | denied | cancelled.' },
      },
      ['scope'],
    ),
    example: 'list_access_requests(scope: "review", status: "pending")',
  },
  {
    name: 'get_access_request',
    title: 'Get access request detail',
    description:
      'Full detail for one access request (an access task) by id: requester, resource, status, assignees (the people who may decide, besides root/maintainer), expiry (expiresAt = the task due date; null = permanent), the submitted answers, and the complete event timeline (assignments, comments, decision). Legacy fields are neutral: currentLayer is always 1, riskTier/maintainerEmail/maintainerSignoff are null. Every role (root, maintainer, engineer, new_member) can read any request, including one filed by someone else. Read this to EVALUATE a request before posting a recommendation.',
    category: 'access',
    inputSchema: obj({ id: { type: 'number', description: 'Access request id (from list_access_requests).' } }, ['id']),
    example: 'get_access_request(id: 42)',
  },
  {
    name: 'comment_access_request',
    title: 'Comment on an access request',
    description:
      'Post a comment on an access request — the intended way for an AI agent to record a RECOMMENDATION (e.g. "recommend approve — scoped, time-boxed" or "recommend deny — over-broad, ask for a narrower scope") for the human reviewer. Markdown supported; @mention a teammate to notify them. This does NOT decide the request — a human still clicks approve/deny in the dashboard. Same visibility rule as get_access_request.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Access request id.' },
        note: { type: 'string', description: 'Your comment / recommendation. Markdown + @mention supported.' },
      },
      ['id', 'note'],
    ),
    example: 'comment_access_request(id: 42, note: "Recommend **approve** — read-only, time-boxed to the incident.")',
  },
  {
    name: 'cancel_access_request',
    title: 'Cancel an access request you filed',
    description:
      'Withdraw a PENDING access request that YOU filed — use this when the need went away (e.g. the work shipped another way) instead of leaving it in the assignees\' queue. Owner-only: you cannot cancel a request somebody else filed, whatever your role. The request is kept with status "cancelled" and its full timeline; nothing is deleted, and the requester is emailed. Cancelling is NOT a decision — there is deliberately no approve/deny tool.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      { id: { type: 'number', description: 'Access request id (one you filed, still pending).' } },
      ['id'],
    ),
    example: 'cancel_access_request(id: 37)',
  },
  {
    name: 'edit_access_request',
    title: 'Edit an access request description',
    description:
      'Edit the DESCRIPTION (reason) of an access request you OWN — the free-form "why do you need access" justification. Owner-only (the original requester); it does not change status or notify assignees. Overwrites the text in place and stamps an edited time. Markdown supported.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Access request id (one you filed).' },
        reason: { type: 'string', description: 'The new description / justification. Markdown supported. Replaces the current text.' },
      },
      ['id', 'reason'],
    ),
    example: 'edit_access_request(id: 42, reason: "Updated: read-only replica access, time-boxed to the incident (~2 days).")',
  },
  {
    name: 'create_task',
    title: 'File a task',
    description:
      'File a plain task (a ticket for engineers: a bug, a chore, a question, a follow-up) as the signed-in user, the PAT owner. Choose the tool by what is asked: access to a company system → create_access_request; anything else → create_task. Assignees must be engineers or above. Labels need an engineer+ role; new members file without labels. Write descriptionMd in the recommended outline (see that field). Returns the task with its id and link.',
    category: 'tasks',
    writes: true,
    inputSchema: obj(
      {
        title: { type: 'string', description: 'Short summary (max 160 characters).', maxLength: 160 },
        descriptionMd: {
          type: 'string',
          description:
            'Details. Markdown supported; @mention a teammate to notify them. Recommended outline (not enforced; skip sections that do not apply, a small chore can be two lines): ## Context (why, related #tasks, goal, settled facts) · ## Problem (with evidence: file:line, logs, counts) · ## Proposal (steps, then **Out of scope:**) · ## Decision (dated, who decided, rejected options) · ## References (code paths, full MR links, related tasks) · ## Done when (a "- [ ]" checklist of verifiable outcomes). One task per topic; steps go in the checklist, not separate tasks.',
        },
        assigneeEmails: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional people to assign (emails, or "Name <email>"). Each must be an engineer or above.',
        },
        labels: { type: 'array', items: { type: 'string' }, description: 'Optional label names (engineer+ only). workflow:: labels are refused.' },
        dueAt: { type: 'string', description: 'Optional due date, ISO 8601 (e.g. 2026-11-01). Must not be in the past.' },
      },
      ['title'],
    ),
    example: 'create_task(title: "Rotate the staging Redis password", descriptionMd: "Leaked in a log line.", dueAt: "2026-11-01")',
  },
  {
    name: 'list_tasks',
    title: 'List tasks',
    description:
      'List the tasks you can see — plain tasks AND access requests, each tagged kind "task" or "access". Same visibility as the dashboard: engineers and above see every task; a new member sees every access request plus the plain tasks they filed or are assigned to. Filters mirror the web list. Newest activity first. Page with `cursor` (returned as nextCursor).',
    category: 'tasks',
    inputSchema: obj({
      state: { type: 'string', enum: ['open', 'closed', 'all'], description: "Defaults to 'open'." },
      scope: {
        type: 'string',
        enum: ['all', 'mine', 'assigned_to_me', 'requested_by_me'],
        description: "Defaults to 'all' (everything you can see). 'mine' = you filed it or are assigned to it.",
      },
      kind: { type: 'string', enum: ['all', 'task', 'access'], description: "Defaults to 'all'." },
      label: { type: 'string', description: 'Only tasks carrying this label name.' },
      q: { type: 'string', description: 'Text search over title, description, people, resource and labels; "#42" finds task 42.' },
      limit: { type: 'number', description: 'Page size, 1-100 (default 25).' },
      cursor: { type: 'string', description: 'The nextCursor of the previous page.' },
    }),
    example: 'list_tasks(scope: "assigned_to_me", state: "open")',
  },
  {
    name: 'get_task',
    title: 'Get task detail',
    description:
      'Full detail for one task by id — plain task or access request: title, description, state and close reason, requester, assignees, labels, due date, the access block for an access request, your permissions on it (canEdit, canCancel, canReopen, canLabel) and the most recent events and comments. A task you cannot see is reported as not found.',
    category: 'tasks',
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Task id (from list_tasks).' },
        eventLimit: { type: 'number', description: 'How many of the most recent events to return, 1-200 (default 50).' },
      },
      ['id'],
    ),
    example: 'get_task(id: 42)',
  },
  {
    name: 'comment_task',
    title: 'Comment on a task',
    description:
      'Add a comment to any task you can see, open or closed — plain task or access request. Markdown supported; @mention a teammate to notify them.',
    category: 'tasks',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Task id.' },
        bodyMd: { type: 'string', description: 'The comment, in markdown.' },
      },
      ['id', 'bodyMd'],
    ),
    example: 'comment_task(id: 42, bodyMd: "Fixed in !123, deploying to qa.")',
  },
  {
    name: 'update_task',
    title: 'Edit a task',
    description:
      'Change a task you may edit, with the dashboard\'s rules. A CLOSED plain task stays closed but still takes every change here (system labels excepted); a CLOSED access request takes only addLabels/removeLabels (non-system labels) — any other field in the same call refuses the whole call. Rules: the requester, an assignee or root may edit (a new member only their own task); assignees must be engineers or above (on an access request, anyone but the requester); labels need an engineer+ role; only the requester edits an access request\'s description. Pass only what changes. Steps run in order (fields, assignees, labels); if one is refused the error says which steps were already applied.',
    category: 'tasks',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Task id.' },
        title: { type: 'string', description: 'New title.', maxLength: 160 },
        descriptionMd: { type: 'string', description: 'New description (replaces the current one). Markdown.' },
        dueAt: { type: 'string', description: 'New due date, ISO 8601. Pass null or an empty string to clear it.' },
        addAssignees: { type: 'array', items: { type: 'string' }, description: 'Emails to assign.' },
        removeAssignees: { type: 'array', items: { type: 'string' }, description: 'Emails to unassign.' },
        addLabels: { type: 'array', items: { type: 'string' }, description: 'Label names to add.' },
        removeLabels: { type: 'array', items: { type: 'string' }, description: 'Label names to remove.' },
      },
      ['id'],
    ),
    example: 'update_task(id: 42, dueAt: "2026-11-15", addAssignees: ["alice@optisigns.com"])',
  },
  {
    name: 'close_task',
    title: 'Close a task',
    description:
      "Close an open task with a reason: 'done', 'not_planned' or 'cancelled'. Dashboard rules: the requester, an assignee or root may close done/not_planned (a new member only their own plain task); cancelled is the requester's (or root's). An access request is withdrawn with cancel_access_request instead, and cannot be closed while it is pending — its approve/deny decision is made in the dashboard.",
    category: 'tasks',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Task id.' },
        reason: { type: 'string', enum: ['done', 'not_planned', 'cancelled'], description: 'Why it is closed.' },
        note: { type: 'string', description: 'Optional closing note, markdown.' },
      },
      ['id', 'reason'],
    ),
    example: 'close_task(id: 42, reason: "done", note: "Shipped in !123.")',
  },
  {
    name: 'reopen_task',
    title: 'Reopen a task',
    description:
      'Reopen a closed task, with the dashboard\'s rules: the requester, an assignee or root (a new member only their own plain task). An access request that was approved or denied cannot be reopened — file a new one.',
    category: 'tasks',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Task id.' },
        note: { type: 'string', description: 'Optional note, markdown.' },
      },
      ['id'],
    ),
    example: 'reopen_task(id: 42, note: "Regressed on qa.")',
  },
  {
    name: 'list_service_alerts',
    title: 'List a service\'s firing alerts',
    description:
      "Currently firing/pending PRODUCTION alerts for one service (or every service when serviceName is omitted), joined against that service's alert selector (set_alert_selector) so each alert is already scoped. Optionally filter by sev. Each alert carries how long it's been active (since). `monitored: false` means Grafana/Prometheus isn't configured in this deployment — the tool degrades to an empty list rather than erroring, so a missing datasource never reads as 'nothing is firing'. Use get_alert_selector to inspect a service's selector, and list_orphan_alerts for firing alerts no selector claims.",
    category: 'alerts',
    inputSchema: obj({
      serviceName: { type: 'string', description: 'Filter to one service (from list_services). Omit for every service.' },
      sev: { type: 'string', description: 'Filter by severity label (e.g. sev1, sev2, sev3).' },
    }),
    example: 'list_service_alerts(serviceName: "optisigns-open-api")',
  },
  {
    name: 'list_orphan_alerts',
    title: 'List unowned firing alerts',
    description:
      "Firing/pending PRODUCTION alerts that match NO service's alert selector — their labels don't line up with any service_alert_selectors row, so nobody's coverage claims them. This is the fleet-wide gap list: pair it with set_alert_selector (root|maintainer) to declare the missing selector. `monitored: false` when Grafana/Prometheus is unconfigured (degrades to empty, never errors).",
    category: 'alerts',
    inputSchema: obj({}),
    example: 'list_orphan_alerts()',
  },
  {
    name: 'get_alert_selector',
    title: "Read a service's alert-label selector",
    description:
      "The service's current alert-label selector: its matcher objects and whether they are auto-'suggested' or human-'confirmed'. Call this BEFORE set_alert_selector — that tool REPLACES the whole matcher list, so read the current one first if you mean to add to it rather than overwrite it. Returns {selector: null} when the service has none yet.",
    category: 'alerts',
    inputSchema: obj(
      { serviceName: { type: 'string', description: 'Service to read (from list_services).' } },
      ['serviceName'],
    ),
    example: 'get_alert_selector(serviceName: "optisigns-open-api")',
  },
  {
    name: 'set_alert_selector',
    title: "Declare a service's alert-label selector",
    description:
      "Declare (or replace) a service's alert-label selector — the Prometheus label matchers (e.g. {cluster, namespace, deployment}) that decide which firing alerts belong to it. `matchers` is a non-empty array of FLAT objects whose values are all strings; an alert matches the selector if ANY one matcher fully matches its labels. A human-set selector (this tool) takes precedence over an auto-suggested one and is never silently overwritten by a later suggestion. Requires a root or maintainer PAT.",
    category: 'alerts',
    writes: true,
    inputSchema: obj(
      {
        serviceName: { type: 'string', description: 'Service to set the selector on (from list_services).' },
        matchers: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Non-empty array of flat {label: value} objects, e.g. [{"cluster":"k8s-optisigns-prod-x","namespace":"production","deployment":"my-svc"}]. Values must all be strings.',
        },
      },
      ['serviceName', 'matchers'],
    ),
    example:
      'set_alert_selector(serviceName: "optisigns-open-api", matchers: [{"cluster": "k8s-optisigns-prod-api-gateway-2", "namespace": "production", "deployment": "scio3-prod-api"}])',
  },
  {
    name: 'get_device_audit_log',
    title: 'Device SecureLink audit log',
    description:
      "One device's SecureLink audit timeline — the same events the web VPN Peers page shows, newest first. Use it to troubleshoot a device: did it provision, rotate its key, connect, move hub, fall back to the relay, and could staff reach it. Identify the device by its device UUID (= the SecureLink peer name). Window: the last `windowDays` days, 1-30, default 30. Each event is {ts, kind, actor?, title, detail?, outcome}; read `actor` + `title` as one sentence. Kinds: `status` = a SecureLink tunnel status change the device reported (connected / disconnected / provisioned); `provisioning` = a provisioning era (one WireGuard key; many short eras = the key-rotation / never-handshake loop); `placement` = a hub move mdm-api requested and the result the device answered; `relay` = a TCP-relay fallback session wg-relay opened and how it ended; `connection` = a staff reach attempt over ssh / adb / vnc, with the staff email as `actor` and whether it was established. `outcome` is ok / fail / neutral. `summary` = {windowDays, attempts, established, successPercent} over the connection attempts; successPercent is absent when nobody tried (not 0%). `auditAvailable: false` means the connection-audit store is not connected, so connection rows are missing rather than zero. `truncated: true` means more events matched than `limit`. Peer-RECORD events (SecureLink allocated, peer record rewritten) are not here — they are only on the web Peers page. Read-only.",
    category: 'vpn',
    inputSchema: obj(
      {
        uuid: { type: 'string', description: 'Device UUID (= the SecureLink peer name), e.g. 3f2b9c1e-8a4d-4e6f-9b21-7c5d0e8a1f34.' },
        windowDays: { type: 'number', description: 'Days back to read, 1-30. Default 30.' },
        kinds: {
          type: 'array',
          items: { type: 'string', enum: DEVICE_AUDIT_LOG_KINDS },
          description: 'Only these kinds. Omit for every kind.',
        },
        limit: {
          type: 'number',
          description: `Max events, newest first. Default ${DEVICE_AUDIT_LOG_DEFAULT_LIMIT}, max ${DEVICE_AUDIT_LOG_MAX_LIMIT}.`,
        },
      },
      ['uuid'],
    ),
    example: 'get_device_audit_log(uuid: "3f2b9c1e-8a4d-4e6f-9b21-7c5d0e8a1f34", windowDays: 7, kinds: ["connection", "relay"])',
  },
];
